import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';

interface Notification {
  id: number;
  type: string;
  title: string;
  message: string;
  related_id: number;
  is_read: boolean;
  created_at: string;
  share_status?: string;
  share_responded_at?: string;
}

interface NotificationCenterProps {
  onClose: () => void;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({ onClose }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState<number | null>(null);
  const [actionSuccess, setActionSuccess] = useState<{action: 'accept' | 'reject', message: string} | null>(null);

  useEffect(() => {
    loadNotifications();
    markAllNotificationsAsRead();
  }, []);

  const markAllNotificationsAsRead = async () => {
    try {
      // Get all unread notifications and mark them as read
      const response = await apiService.getNotifications();
      const unreadNotifications = response.notifications.filter(n => !n.is_read);
      
      // Mark all unread notifications as read
      for (const notification of unreadNotifications) {
        await apiService.markNotificationRead(notification.id);
      }
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  const loadNotifications = async () => {
    try {
      const response = await apiService.getNotifications();
      setNotifications(response.notifications);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleShareResponse = async (shareId: number, action: 'accept' | 'reject') => {
    setResponding(shareId);
    try {
      const response = await apiService.respondToShare(shareId, action);
      // Reload notifications to see the updated status
      await loadNotifications();
      
      // Show success message
      setActionSuccess({
        action,
        message: response.message
      });
      
      // Auto-close modal after 2 seconds and clear notifications
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Failed to respond to share:', error);
    } finally {
      setResponding(null);
    }
  };

  const markAsRead = async (notificationId: number) => {
    try {
      await apiService.markNotificationRead(notificationId);
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId ? { ...notif, is_read: true } : notif
        )
      );
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays}d ago`;
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'dictionary_share_received':
        return '📚';
      case 'dictionary_share_accepted':
        return '✅';
      case 'dictionary_share_rejected':
        return '❌';
      default:
        return '📢';
    }
  };

  if (loading) {
    return (
      <div className="auth-form" style={{ textAlign: 'center' }}>
        <h2>Notifications</h2>
        <p>Loading...</p>
      </div>
    );
  }

  if (actionSuccess) {
    return (
      <div className="auth-form">
        <h2>{actionSuccess.action === 'accept' ? 'Game Accepted!' : 'Game Declined'}</h2>
        <div className="share-form-header">
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
            {actionSuccess.action === 'accept' ? '✅' : '❌'}
          </div>
          <p className={`action-success-text ${actionSuccess.action}`}>
            {actionSuccess.message}
          </p>
          <p className="share-countdown-text">
            Closing automatically in 2 seconds...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-form">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2 style={{ margin: 0 }}>Notifications</h2>
        <button
          onClick={onClose}
          className="notification-close-btn"
        >
          ✕
        </button>
      </div>

      {notifications.length === 0 ? (
        <div className="notification-empty-text">
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
          <p>No notifications yet</p>
        </div>
      ) : (
        <div className="notification-list">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className={`notification-item ${notification.is_read ? 'read' : 'unread'}`}
              onClick={() => !notification.is_read && markAsRead(notification.id)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '1.2rem', marginRight: '0.5rem' }}>
                      {getNotificationIcon(notification.type)}
                    </span>
                    <h4 className="notification-title">
                      {notification.title}
                    </h4>
                  </div>
                  <p className="notification-text">
                    {notification.message}
                  </p>
                  <small className="notification-date">
                    {formatDate(notification.created_at)}
                  </small>
                </div>
                
                {!notification.is_read && (
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#4CAF50',
                    marginLeft: '1rem'
                  }} />
                )}
              </div>

              {/* Show accept/reject buttons for pending share requests or status for responded ones */}
              {notification.type === 'dictionary_share_received' && (
                <div style={{ marginTop: '1rem' }}>
                  {notification.share_status === 'pending' || !notification.share_status ? (
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShareResponse(notification.related_id, 'accept');
                        }}
                        disabled={responding === notification.related_id}
                        style={{
                          backgroundColor: '#4CAF50',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.8rem'
                        }}
                      >
                        {responding === notification.related_id ? 'Processing...' : 'Accept'}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShareResponse(notification.related_id, 'reject');
                        }}
                        disabled={responding === notification.related_id}
                        style={{
                          backgroundColor: '#f44336',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.8rem'
                        }}
                      >
                        {responding === notification.related_id ? 'Processing...' : 'Decline'}
                      </button>
                    </div>
                  ) : (
                    <div style={{
                      padding: '8px 12px',
                      borderRadius: '4px',
                      backgroundColor: notification.share_status === 'accept' ? '#4CAF50' : '#f44336',
                      color: 'white',
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      textAlign: 'center',
                      display: 'inline-block'
                    }}>
                      {notification.share_status === 'accept' ? '✅ Accepted' : '❌ Declined'}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;